import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Globe } from "lucide-react";
import { Logo } from "@/components/Logo";
import { useLang } from "@/context/LanguageContext";

export const Navbar = () => {
  const { t, lang, toggle } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [pathname]);

  const isHome = pathname === "/";
  // Header is transparent over a dark PageHeader on every page except Home
  const onDark = !scrolled && !isHome;

  const links = [
    { to: "/", label: t.nav.home },
    { to: "/services", label: t.nav.services },
    { to: "/cabinet", label: t.nav.about },
    { to: "/contact", label: t.nav.contact },
  ];

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.1 }}
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-500 ${
        scrolled ? "bg-ivory/80 backdrop-blur-xl border-b border-gold/20" : "bg-transparent"
      }`}
      data-testid="navbar"
    >
      <div className="max-w-[1500px] mx-auto px-6 lg:px-12 h-20 flex items-center justify-between">
        <Link to="/" data-testid="logo-link" className="flex items-center">
          <Logo className="h-10 lg:h-11" light={onDark} />
        </Link>

        <nav className="hidden lg:flex items-center gap-10">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={`nav-${l.to === "/" ? "home" : l.to.slice(1)}`}
              className={({ isActive }) =>
                `nav-link text-[13px] uppercase tracking-[0.18em] font-medium ${
                  onDark ? "text-ivory/80 hover:text-ivory" : "text-navy/80 hover:text-navy"
                } ${isActive ? `active ${onDark ? "text-ivory" : "text-navy"}` : ""}`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-4">
          <button
            onClick={toggle}
            data-testid="lang-toggle"
            className={`hidden sm:flex items-center gap-2 text-[12px] uppercase tracking-[0.18em] font-semibold hover:text-gold transition-colors ${
              onDark ? "text-ivory/80" : "text-navy/70"
            }`}
          >
            <Globe size={15} strokeWidth={1.5} />
            <span className={lang === "fr" ? "text-gold" : ""}>FR</span>
            <span className={onDark ? "text-ivory/40" : "text-navy/30"}>/</span>
            <span className={lang === "en" ? "text-gold" : ""}>EN</span>
          </button>

          <Link
            to="/contact"
            data-testid="nav-book-btn"
            className={`hidden lg:inline-flex px-6 py-3 text-[12px] uppercase tracking-[0.18em] font-semibold ${
              onDark ? "btn-outline-gold border-gold text-ivory hover:text-navy" : "btn-primary"
            }`}
          >
            {t.nav.book}
          </Link>

          <button
            className={`lg:hidden p-2 ${onDark ? "text-ivory" : "text-navy"}`}
            onClick={() => setOpen((o) => !o)}
            data-testid="mobile-menu-btn"
            aria-label="Menu"
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="lg:hidden overflow-hidden bg-ivory border-b border-gold/20"
            data-testid="mobile-menu"
          >
            <div className="px-6 py-8 flex flex-col gap-6">
              {links.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  end={l.to === "/"}
                  className="font-serif text-3xl text-navy"
                  data-testid={`mobile-nav-${l.to === "/" ? "home" : l.to.slice(1)}`}
                >
                  {l.label}
                </NavLink>
              ))}
              <div className="flex items-center gap-4 pt-4 border-t border-gold/20">
                <button onClick={toggle} data-testid="mobile-lang-toggle" className="flex items-center gap-2 text-sm uppercase tracking-widest font-semibold text-navy">
                  <Globe size={16} />
                  <span className={lang === "fr" ? "text-gold" : ""}>FR</span> / <span className={lang === "en" ? "text-gold" : ""}>EN</span>
                </button>
              </div>
              <Link to="/contact" className="btn-primary px-6 py-4 text-center text-sm uppercase tracking-widest font-semibold" data-testid="mobile-book-btn">
                {t.nav.book}
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
